/**
 * Union schemas for RT Messaging WebSocket validation
 *
 * Combines generated Zod schemas into union types matching
 * the TypeScript union types in rt-messaging.generated.ts
 */

import { z } from "zod";
import {
  // Response schemas
  unsubscribedSchema,
  markedSchema,
  completeSchema,
  // Push notification schemas
  emptySchema,
} from "./rt-messaging.generated";

/**
 * Union schema for all server → client response messages
 * Validates any message that could be a response to a client operation
 */
export const rtMessagingResponseSchema = z.union([
  // Note: We use z.any() here because ts-to-zod doesn't generate schemas
  // for complex nested types (ChannelList, SubscriptionList, etc.)
  // In production, you would want to manually create complete schemas
  z.object({ message: z.literal("channel-list") }).passthrough(),
  z.object({ message: z.literal("subscription-list") }).passthrough(),
  z.object({ message: z.literal("subscribed") }).passthrough(),
  unsubscribedSchema,
  z.object({ message: z.literal("new-entries") }).passthrough(),
  z.object({ message: z.literal("entries") }).passthrough(),
  markedSchema,
  z.object({ message: z.literal("current-editor") }).passthrough(),
  z.object({ message: z.literal("data-changed") }).passthrough(),
  completeSchema,
  z.object({ message: z.literal("Error") }).passthrough(),
]);

/**
 * Union schema for all server → client push notifications (no trans_id)
 * Validates messages that are server-initiated, not responses to requests
 */
export const rtMessagingPushSchema = z.union([
  z.object({ message: z.literal("new-entries") }).passthrough(),
  z.object({ message: z.literal("current-editor") }).passthrough(),
  z.object({ message: z.literal("data-changed") }).passthrough(),
  // SCRUM-788: job/step execution events
  z.object({ message: z.literal("job-event") }).passthrough(),
  emptySchema,
]);

/**
 * Union schema for any RT Messaging message (response or push)
 * Use this for general message validation
 */
export const rtMessagingAnyMessageSchema = z.union([
  rtMessagingResponseSchema,
  rtMessagingPushSchema,
]);
